import type {QueryClient} from '@tanstack/react-query';
import {showErrorSnackBar} from '../../../shared/components';
import {CACHE_KEYS} from './useEntryManagementUpdate';

/**
 * エントリー管理のステップ解析処理で使用する共通ヘルパー関数
 */

type AnalyzeStepParams = {
  companyID?: number;
  userEntryManagementID?: number;
};

/**
 * ステップ解析APIのパラメータを構築する
 *
 * @param companyId - 企業ID（0の場合は除外）
 * @param entryManagementId - エントリー管理ID（undefinedの場合は除外）
 * @returns ステップ解析APIに渡すパラメータオブジェクト
 */
export const buildAnalyzeStepParams = (
  companyId?: number,
  entryManagementId?: number,
): AnalyzeStepParams => {
  const params: AnalyzeStepParams = {};

  // companyIdが有効な値（0以外）の場合のみ追加
  if (companyId && companyId !== 0) {
    params.companyID = companyId;
  }

  // entryManagementIdがundefinedでない場合のみ追加
  if (entryManagementId !== undefined) {
    params.userEntryManagementID = entryManagementId;
  }

  return params;
};

/**
 * 解析成功時のキャッシュ更新処理
 * エントリー管理一覧と詳細のクエリを無効化して再取得を促す
 *
 * @param queryClient - React QueryのQueryClientインスタンス
 * @param entryManagementId - エントリー管理ID（ステップ追加時のみ指定）
 */
export const invalidateAnalyzeResultQueries = async (
  queryClient: QueryClient,
  entryManagementId?: number,
): Promise<void> => {
  // エントリー管理一覧を常に再取得
  await queryClient.invalidateQueries({
    queryKey: [CACHE_KEYS.PAGINATED_ENTRY_MANAGEMENTS],
  });
  await queryClient.invalidateQueries({
    queryKey: [CACHE_KEYS.ENTRY_MANAGEMENTS],
  });

  // エントリー管理詳細のデータを再取得（ステップ追加の場合）
  if (entryManagementId) {
    await queryClient.invalidateQueries({
      queryKey: [CACHE_KEYS.ENTRY_MANAGEMENT, entryManagementId],
    });
  }
};

/**
 * 解析処理のエラー表示
 *
 * @param importType - インポート方法（'画像' | 'テキスト'）
 * @param error - エラーオブジェクト
 */
export const showAnalyzeError = (
  importType: '画像' | 'テキスト',
  error?: unknown,
): void => {
  if (error) {
    console.error(`${importType}解析エラー:`, error);
  }
  showErrorSnackBar({
    title: `${importType}からの解析に失敗しました`,
    message:
      '企業名または選考予定の情報が不足しています。入力をご確認ください。',
  });
};
